import { createContext, useContext, useState, useCallback, useRef, useEffect } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const RobotContext = createContext(null);

// getPrimaryServices() only exposes services declared here, so list every common
// BLE serial/UART service so we can find writable + notify characteristics on most robots.
const OPTIONAL_SERVICES = [
  0xffe0, 0xffe5, 0xfff0, 0xff00, 0xffb0, 0xffa0, 0xffd0, 0xffc0, 0xfee0, 0xfee7,
  "battery_service",
  "device_information",
  "6e400001-b5a3-f393-e0a9-e50e24dcca9e", // Nordic UART Service (NUS)
  "49535343-fe7d-4ae5-8fa9-9fafd205e455", // Microchip / ISSC (RN4870/71, HM-13)
  "0000ffe0-0000-1000-8000-00805f9b34fb", // HM-10 / CC254x UART
  "0000fff0-0000-1000-8000-00805f9b34fb", // common serial passthrough
  "0000ff00-0000-1000-8000-00805f9b34fb",
  "f000c0e0-0451-4000-b000-000000000000", // TI
  "0000abf0-0000-1000-8000-00805f9b34fb", // ESP32 BLE-serial libraries
];

// Long-form metric names map to the short threshold keys used in the alerts dialog.
const METRIC_ALIAS = { distance: "dist", temperature: "temp", battery: "batt", bearing: "heading" };

export function RobotProvider({ children }) {
  const [status, setStatus] = useState("disconnected"); // disconnected | pairing | connected
  const [deviceName, setDeviceName] = useState("");
  const [logs, setLogs] = useState([]);
  const [telemetry, setTelemetry] = useState({}); // metric -> { value, ts }
  const [telemetryHistory, setTelemetryHistory] = useState([]); // [{ ts, label, <metric>:num }]
  const [simulating, setSimulating] = useState(false);
  const [thresholds, setThresholds] = useState({}); // metric -> { min, max }
  const [breaches, setBreaches] = useState({}); // metric -> { type, limit, value }
  const deviceRef = useRef(null);
  const charRef = useRef(null);
  const notifyRef = useRef(null);
  const simRef = useRef(null);
  const thresholdsRef = useRef({});
  const breachRef = useRef({});
  const disconnectHandlerRef = useRef(null);

  const pushLog = useCallback(async (message, kind = "command", logStatus = "info") => {
    const optimistic = {
      id: `local-${Date.now()}-${Math.random()}`,
      message,
      kind,
      status: logStatus,
      timestamp: new Date().toISOString(),
    };
    setLogs((prev) => [optimistic, ...prev].slice(0, 200));
    try {
      await api.post("/logs", { message, kind, status: logStatus });
    } catch (e) {
      /* keep optimistic entry */
    }
  }, []);

  const applyThresholds = useCallback((t) => {
    thresholdsRef.current = t || {};
    setThresholds(t || {});
  }, []);

  const loadThresholds = useCallback(async () => {
    try {
      const res = await api.get("/settings");
      applyThresholds(res.data.thresholds || {});
    } catch (e) {
      /* not authed yet / ignore */
    }
  }, [applyThresholds]);

  const saveThresholds = useCallback(
    async (t) => {
      applyThresholds(t);
      try {
        await api.put("/settings", { thresholds: t });
      } catch (e) {
        /* ignore */
      }
    },
    [applyThresholds]
  );

  // Compare incoming values against configured min/max; log warnings on new breaches.
  const evaluateAlerts = useCallback(
    (parsed) => {
      const th = thresholdsRef.current || {};
      const next = { ...breachRef.current };
      for (const [k, v] of Object.entries(parsed)) {
        if (typeof v !== "number") continue;
        const rule = th[k] || th[METRIC_ALIAS[k]];
        if (!rule) continue;
        const { min, max } = rule;
        let breach = null;
        if (min != null && min !== "" && v < Number(min)) breach = { type: "min", limit: Number(min), value: v };
        else if (max != null && max !== "" && v > Number(max)) breach = { type: "max", limit: Number(max), value: v };
        const was = breachRef.current[k];
        if (breach && !was) {
          pushLog(`⚠ ALERT: ${k} = ${v} ${breach.type === "min" ? "below min" : "above max"} ${breach.limit}`, "system", "error");
          next[k] = breach;
        } else if (breach) {
          next[k] = breach;
        } else if (!breach && was) {
          pushLog(`✓ ${k} back to normal (${v})`, "system", "success");
          delete next[k];
        }
      }
      breachRef.current = next;
      setBreaches(next);
    },
    [pushLog]
  );

  // Parse an incoming telemetry frame: JSON object OR "key:val,key=val;..." pairs.
  const ingestTelemetry = useCallback((text) => {
    const parsed = {};
    const trimmed = (text || "").trim();
    if (!trimmed) return;
    try {
      const obj = JSON.parse(trimmed);
      if (obj && typeof obj === "object" && !Array.isArray(obj)) Object.assign(parsed, obj);
    } catch (e) {
      trimmed.split(/[,;\n]+/).forEach((pair) => {
        const m = pair.split(/[:=]/);
        if (m.length >= 2 && m[0].trim()) {
          const num = parseFloat(m[1]);
          parsed[m[0].trim()] = isNaN(num) ? m[1].trim() : num;
        }
      });
    }
    if (Object.keys(parsed).length === 0) return;
    const ts = Date.now();
    setTelemetry((prev) => {
      const next = { ...prev };
      for (const [k, v] of Object.entries(parsed)) next[k] = { value: v, ts };
      return next;
    });
    setTelemetryHistory((prev) => {
      const entry = { ts, label: new Date(ts).toLocaleTimeString().split(" ")[0] };
      for (const [k, v] of Object.entries(parsed)) if (typeof v === "number") entry[k] = v;
      return [...prev, entry].slice(-40);
    });
    evaluateAlerts(parsed);
  }, [evaluateAlerts]);

  const handleNotify = useCallback(
    (e) => {
      try {
        const text = new TextDecoder().decode(e.target.value);
        ingestTelemetry(text);
      } catch (err) {
        /* ignore malformed frame */
      }
    },
    [ingestTelemetry]
  );

  // Single discovery pass: locate a writable AND a notify characteristic.
  const discoverCharacteristics = useCallback(
    async (device) => {
      charRef.current = null;
      notifyRef.current = null;
      try {
        const services = await device.gatt.getPrimaryServices();
        for (const service of services) {
          const chars = await service.getCharacteristics();
          for (const c of chars) {
            if (!charRef.current && (c.properties.write || c.properties.writeWithoutResponse)) {
              charRef.current = c;
            }
            if (!notifyRef.current && (c.properties.notify || c.properties.indicate)) {
              try {
                await c.startNotifications();
                c.addEventListener("characteristicvaluechanged", handleNotify);
                notifyRef.current = c;
              } catch (err) {
                /* notify not startable */
              }
            }
          }
        }
      } catch (e) {
        /* characteristics not enumerable */
      }
      return { write: !!charRef.current, notify: !!notifyRef.current };
    },
    [handleNotify]
  );

  const loadLogs = useCallback(async () => {
    try {
      const res = await api.get("/logs");
      setLogs(res.data);
    } catch (e) {
      /* ignore */
    }
  }, []);

  const clearLogs = useCallback(async () => {
    setLogs([]);
    try {
      await api.delete("/logs");
    } catch (e) {
      /* ignore */
    }
  }, []);

  const connectBluetooth = useCallback(async (hint) => {
    if (!navigator.bluetooth) {
      pushLog("Web Bluetooth not supported in this browser. Use Chrome/Edge over HTTPS.", "connection", "error");
      setStatus("disconnected");
      return { ok: false, reason: "unsupported" };
    }
    setStatus("pairing");
    pushLog(hint ? `Pairing via QR target: ${hint}` : "Scanning for BLE robot devices…", "connection", "info");
    try {
      const device = await navigator.bluetooth.requestDevice({
        acceptAllDevices: true,
        optionalServices: OPTIONAL_SERVICES,
      });
      deviceRef.current = device;
      setDeviceName(device.name || "Unknown Device");
      const onDisconnect = () => {
        setStatus("disconnected");
        pushLog(`Lost connection to ${device.name || "device"}`, "connection", "error");
      };
      disconnectHandlerRef.current = onDisconnect;
      device.addEventListener("gattserverdisconnected", onDisconnect);
      await device.gatt.connect();
      const found = await discoverCharacteristics(device);
      setStatus("connected");
      pushLog(`Connected to ${device.name || "robot"}`, "connection", "success");
      if (found.notify) pushLog("Telemetry stream online — receiving sensor data", "system", "success");
      else if (found.write) pushLog("Connected — no telemetry stream found, but you can send commands", "system", "warning");
      else pushLog("Connected, but no readable/writable characteristic was exposed by this device", "system", "warning");
      return { ok: true };
    } catch (e) {
      setStatus("disconnected");
      pushLog(`Connection failed: ${e.message}`, "connection", "error");
      return { ok: false, reason: e.message };
    }
  }, [pushLog, discoverCharacteristics]);

  const disconnect = useCallback(() => {
    try {
      if (deviceRef.current && disconnectHandlerRef.current) {
        deviceRef.current.removeEventListener("gattserverdisconnected", disconnectHandlerRef.current);
      }
      if (notifyRef.current) {
        notifyRef.current.removeEventListener("characteristicvaluechanged", handleNotify);
        notifyRef.current.stopNotifications().catch(() => {});
      }
      if (deviceRef.current?.gatt?.connected) deviceRef.current.gatt.disconnect();
    } catch (e) {
      /* ignore */
    }
    disconnectHandlerRef.current = null;
    deviceRef.current = null;
    charRef.current = null;
    notifyRef.current = null;
    setStatus("disconnected");
    setDeviceName("");
    pushLog("Disconnected by operator", "connection", "warning");
  }, [pushLog, handleNotify]);

  // Demo telemetry generator for use without real hardware.
  const startSimulation = useCallback(() => {
    if (simRef.current) return;
    setSimulating(true);
    pushLog("Telemetry simulation started", "system", "info");
    let heading = 90;
    simRef.current = setInterval(() => {
      heading = (heading + (Math.random() * 24 - 12) + 360) % 360;
      const frame = `dist:${(15 + Math.random() * 185).toFixed(0)},temp:${(21 + Math.random() * 8).toFixed(1)},batt:${(55 + Math.random() * 45).toFixed(0)},heading:${heading.toFixed(0)}`;
      ingestTelemetry(frame);
    }, 1200);
  }, [ingestTelemetry, pushLog]);

  const stopSimulation = useCallback(() => {
    if (simRef.current) {
      clearInterval(simRef.current);
      simRef.current = null;
    }
    setSimulating(false);
    pushLog("Telemetry simulation stopped", "system", "warning");
  }, [pushLog]);

  // Wipe all in-memory robot state (called on user change / logout) so nothing leaks between sessions.
  const resetRobot = useCallback(() => {
    if (simRef.current) {
      clearInterval(simRef.current);
      simRef.current = null;
    }
    try {
      if (deviceRef.current && disconnectHandlerRef.current) {
        deviceRef.current.removeEventListener("gattserverdisconnected", disconnectHandlerRef.current);
      }
      if (notifyRef.current) {
        notifyRef.current.removeEventListener("characteristicvaluechanged", handleNotify);
        notifyRef.current.stopNotifications().catch(() => {});
      }
      if (deviceRef.current?.gatt?.connected) deviceRef.current.gatt.disconnect();
    } catch (e) {
      /* ignore */
    }
    disconnectHandlerRef.current = null;
    deviceRef.current = null;
    charRef.current = null;
    notifyRef.current = null;
    breachRef.current = {};
    thresholdsRef.current = {};
    setStatus("disconnected");
    setDeviceName("");
    setSimulating(false);
    setLogs([]);
    setTelemetry({});
    setTelemetryHistory([]);
    setBreaches({});
    setThresholds({});
  }, [handleNotify]);

  const { user } = useAuth();
  const prevUserId = useRef(undefined);
  useEffect(() => {
    const uid = user && user.id ? user.id : null;
    if (prevUserId.current !== undefined && prevUserId.current !== uid) {
      resetRobot();
    }
    prevUserId.current = uid;
  }, [user, resetRobot]);

  const sendCommand = useCallback(async (payload, label) => {
    const name = label || payload;
    if (status !== "connected") {
      pushLog(`TX BLOCKED [${name}] — robot not connected`, "command", "error");
      return { ok: false };
    }
    // Attempt real BLE write if a writable characteristic was discovered
    try {
      if (charRef.current) {
        const enc = new TextEncoder();
        await charRef.current.writeValue(enc.encode(payload + "\n"));
        pushLog(`TX ▸ ${name}  [${payload}]`, "command", "success");
      } else {
        pushLog(`TX ▸ ${name}  [${payload}] — no writable BLE characteristic on device`, "command", "warning");
      }
      return { ok: true };
    } catch (e) {
      pushLog(`TX error [${name}]: ${e.message}`, "command", "error");
      return { ok: false };
    }
  }, [status, pushLog]);

  return (
    <RobotContext.Provider
      value={{
        status,
        deviceName,
        logs,
        telemetry,
        telemetryHistory,
        simulating,
        thresholds,
        breaches,
        loadThresholds,
        saveThresholds,
        connectBluetooth,
        disconnect,
        sendCommand,
        pushLog,
        loadLogs,
        clearLogs,
        startSimulation,
        stopSimulation,
      }}
    >
      {children}
    </RobotContext.Provider>
  );
}

export const useRobot = () => useContext(RobotContext);
